import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  EyeIcon,
  TrashIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  TruckIcon,
  UserIcon,
  CubeIcon,
  ChevronDownIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
  BuildingStorefrontIcon,
  InformationCircleIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { API_URL } from '../../Config';

const MyEvenements = () => {
  const navigate = useNavigate();
  const [evenements, setEvenements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedEvenement, setSelectedEvenement] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('tous');
  const pdfRef = useRef();

  useEffect(() => {
    chargerEvenements();
  }, []);

  const chargerEvenements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await axios.get(`${API_URL}/my-evenements`, config);
      console.log('Événements reçus:', response.data);
      setEvenements(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement événements:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors du chargement de vos événements'
      });
      setOpenSnackbar(true);
      setEvenements([]);
    } finally {
      setLoading(false);
    }
  };

  // Fonction d'export PDF
  const exportToPDF = async (evenement = null) => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      
      if (evenement) {
        // Export d'un seul événement
        await generateEvenementPDF(doc, evenement);
      } else {
        // Export de tous les événements
        await generateAllEvenementsPDF(doc);
      }
      
      const fileName = evenement 
        ? `evenement-${evenement.id}.pdf`
        : `mes-evenements-${new Date().toISOString().split('T')[0]}.pdf`;
      
      doc.save(fileName);
      
      setMessage({
        type: 'success',
        text: evenement 
          ? 'PDF exporté avec succès'
          : 'Tous les événements ont été exportés en PDF'
      });
      setOpenSnackbar(true);
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors de l\'export PDF'
      });
      setOpenSnackbar(true);
    }
  };

  const generateEvenementPDF = async (doc, evenement) => {
    // En-tête du PDF
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 139); // Bleu foncé
    doc.text('Détails de l\'événement', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`N°: ${evenement.id}`, 20, 35);
    doc.text(`Date de création: ${formatDate(evenement.created_at)}`, 20, 42);
    
    // Informations principales
    doc.setFontSize(14);
    doc.setTextColor(0, 102, 204);
    doc.text('Informations principales', 20, 55);
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    let y = 62;
    
    doc.text(`Statut: ${getStatusLabel(evenement.statut)}`, 20, y);
    y += 6;
    doc.text(`Type: ${evenement.type === 'enterrement' ? 'Enterrement' : 'Autre'}`, 20, y);
    y += 6;
    doc.text(`Transport: ${evenement.transport ? 'Oui' : 'Non'}`, 20, y);
    y += 8;
    
    // Bénéficiaire
    if (evenement.beneficiaire) {
      doc.setFontSize(12);
      doc.setTextColor(0, 102, 204);
      doc.text('Bénéficiaire', 20, y);
      y += 6;
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Nom: ${evenement.beneficiaire.nom}`, 20, y);
      y += 6;
      doc.text(`Type: ${evenement.beneficiaire.type === 'association' ? 'Association' : 'Particulier'}`, 20, y);
      y += 6;
      doc.text(`Contact: ${evenement.beneficiaire.contact || 'N/A'}`, 20, y);
      y += 8;
    }
    
    // Pack d'enterrement
    if (evenement.pack_enterrement) {
      doc.setFontSize(12);
      doc.setTextColor(0, 102, 204);
      doc.text('Pack d\'enterrement', 20, y);
      y += 6;
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Nom: ${evenement.pack_enterrement.nom}`, 20, y);
      y += 6;
      doc.text(`Description: ${evenement.pack_enterrement.description || 'N/A'}`, 20, y);
      y += 8;
    }
    
    // Matériels
    if (evenement.materiels && evenement.materiels.length > 0) {
      doc.setFontSize(12);
      doc.setTextColor(0, 102, 204);
      doc.text('Matériels', 20, y);
      y += 8;
      
      // En-tête du tableau
      doc.setFillColor(240, 240, 240);
      doc.rect(20, y, 170, 8, 'F');
      doc.setTextColor(0, 0, 0);
      doc.text('Nom', 22, y + 6);
      doc.text('Quantité', 120, y + 6);
      doc.text('Unité', 150, y + 6);
      y += 10;
      
      // Lignes du tableau
      evenement.materiels.forEach((materiel, index) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        
        doc.text(materiel.nom.substring(0, 40), 22, y + 6);
        doc.text(materiel.pivot.quantite_demandee.toString(), 120, y + 6);
        doc.text(materiel.unite || 'unité', 150, y + 6);
        y += 8;
      });
    }
    
    // Pied de page
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Exporté le ${new Date().toLocaleDateString('fr-FR')}`, 105, 290, { align: 'center' });
  };

  const generateAllEvenementsPDF = async (doc) => {
    // En-tête du PDF
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 139);
    doc.text('Mes Événements', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Total: ${filteredEvenements.length} événement(s)`, 20, 35);
    doc.text(`Date d'export: ${new Date().toLocaleDateString('fr-FR')}`, 20, 42);
    
    // Tableau des événements
    let y = 55;
    
    // En-tête du tableau
    doc.setFontSize(10);
    doc.setFillColor(240, 240, 240);
    doc.rect(20, y, 170, 8, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('N°', 22, y + 6);
    doc.text('Statut', 40, y + 6);
    doc.text('Type', 70, y + 6);
    doc.text('Date création', 100, y + 6);
    doc.text('Bénéficiaire', 140, y + 6);
    y += 10;
    
    // Lignes du tableau
    filteredEvenements.forEach((evenement, index) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
        // Réafficher l'en-tête sur la nouvelle page
        doc.setFillColor(240, 240, 240);
        doc.rect(20, y, 170, 8, 'F');
        doc.text('N°', 22, y + 6);
        doc.text('Statut', 40, y + 6);
        doc.text('Type', 70, y + 6);
        doc.text('Date création', 100, y + 6);
        doc.text('Bénéficiaire', 140, y + 6);
        y += 10;
      }
      
      doc.setFont('helvetica', 'normal');
      doc.text(evenement.id.toString(), 22, y + 6);
      doc.text(getStatusLabel(evenement.statut), 40, y + 6);
      doc.text(evenement.type === 'enterrement' ? 'Enterrement' : 'Autre', 70, y + 6);
      doc.text(formatDate(evenement.created_at), 100, y + 6);
      doc.text(evenement.beneficiaire?.nom.substring(0, 15) || 'N/A', 140, y + 6);
      y += 8;
    });
    
    // Pied de page
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Exporté le ${new Date().toLocaleDateString('fr-FR')}`, 105, 290, { align: 'center' });
  };

  const getStatusLabel = (statut) => {
    const labels = {
      valide: 'Validé',
      refuse: 'Refusé',
      en_attente: 'En attente'
    };
    return labels[statut] || statut;
  };

  const getStatutBadge = (statut) => {
    const styles = {
      valide: 'bg-green-100 text-green-800 border-green-300',
      refuse: 'bg-red-100 text-red-800 border-red-300',
      en_attente: 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };

    const icons = {
      valide: <CheckCircleIcon className="h-4 w-4 mr-1" />,
      refuse: <XCircleIcon className="h-4 w-4 mr-1" />,
      en_attente: <ClockIcon className="h-4 w-4 mr-1" />
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${styles[statut] || styles.en_attente}`}>
        {icons[statut] || icons.en_attente}
        {getStatusLabel(statut)}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
        type === 'enterrement' 
          ? 'bg-blue-100 text-blue-800 border border-blue-300' 
          : 'bg-gray-100 text-gray-800 border border-gray-300'
      }`}>
        {type === 'enterrement' ? 'Enterrement' : 'Autre'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewDetails = (evenement) => {
    setSelectedDetails(evenement);
    setDetailsDialogOpen(true);
  };

  const handleDeleteClick = (evenement) => {
    setSelectedEvenement(evenement);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedEvenement) return;

    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      await axios.delete(`${API_URL}/evenements/${selectedEvenement.id}`, config);
      
      setMessage({
        type: 'success',
        text: 'Événement supprimé avec succès'
      });
      setOpenSnackbar(true);
      
      chargerEvenements();
    } catch (error) {
      console.error('Erreur suppression:', error);
      setMessage({
        type: 'error',
        text: 'Erreur lors de la suppression'
      });
      setOpenSnackbar(true);
    } finally {
      setOpenDeleteDialog(false);
      setSelectedEvenement(null);
    }
  };

  const handleCreateNew = () => {
    navigate('/client/evenements/nouveau');
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedDetails(null);
  };

  // Filtrage des événements
  const filteredEvenements = evenements.filter(evenement => {
    const matchesSearch = 
      evenement.id.toString().includes(searchTerm.toLowerCase()) ||
      (evenement.beneficiaire?.nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      evenement.type.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'tous' || evenement.statut === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center ">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Chargement de vos événements...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen  py-6 px-4 sm:px-6 lg:px-8">
      {/* En-tête */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center mb-2">
              <CalendarIcon className="h-8 w-8 text-blue-600 mr-3" />
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Mes Événements</h1>
            </div>
            <p className="text-gray-600">
              {filteredEvenements.length} événement(s) trouvé(s) sur {evenements.length} total
            </p>
          </div>
          
          <button
            onClick={handleCreateNew}
            className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition flex items-center"
          >
            <CalendarIcon className="h-5 w-5 mr-2" />
            Créer un nouvel événement
          </button>
        </div>
      </div>

      {/* Filtres et export */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Recherche */}
            <div className="flex-1">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Rechercher
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par ID, bénéficiaire ou type..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                />
                <div className="absolute left-3 top-2.5">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Filtre par statut */}
            <div>
              <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700 mb-2">
                Filtrer par statut
              </label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full md:w-40 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              >
                <option value="tous">Tous les statuts</option>
                <option value="en_attente">En attente</option>
                <option value="valide">Validé</option>
                <option value="refuse">Refusé</option>
              </select>
            </div>

            {/* Boutons d'export */}
            <div className="flex items-end gap-2">
              <button
                onClick={() => exportToPDF()}
                className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition flex items-center"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                Export PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau des événements */}
      <div className="max-w-7xl mx-auto">
        {filteredEvenements.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun événement trouvé</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || statusFilter !== 'tous' 
                ? 'Aucun événement ne correspond à vos critères de recherche'
                : 'Vous n\'avez pas encore créé d\'événement.'}
            </p>
            <button
              onClick={handleCreateNew}
              className="px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
            >
              Créer mon premier événement
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      ID
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Statut
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Bénéficiaire
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Date création
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Matériels
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Transport
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEvenements.map((evenement) => (
                    <tr key={evenement.id} className="hover:">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">#{evenement.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatutBadge(evenement.statut)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getTypeBadge(evenement.type)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {evenement.beneficiaire?.nom || null}
                            </div>
                            <div className="text-xs text-gray-500">
                              {evenement.beneficiaire?.type === 'association' ? 'Association' : 'Particulier'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatDate(evenement.created_at)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {evenement.materiels?.length || 0} matériel(s)
                        </div>
                        {evenement.pack_enterrement && (
                          <div className="text-xs text-gray-500 flex items-center mt-1">
                            <CubeIcon className="h-3 w-3 mr-1" />
                            Pack: {evenement.pack_enterrement.nom}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {evenement.transport ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            <TruckIcon className="h-3 w-3 mr-1" />
                            Oui
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Non
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewDetails(evenement)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="Voir les détails"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => exportToPDF(evenement)}
                            className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                            title="Exporter en PDF"
                          >
                            <DocumentArrowDownIcon className="h-5 w-5" />
                          </button>
                          {evenement.statut === 'en_attente' && (
                            <button
                              onClick={() => handleDeleteClick(evenement)}
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                              title="Supprimer"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal de détails */}
      {detailsDialogOpen && selectedDetails && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div 
              className="fixed inset-0 0 bg-opacity-75 transition-opacity" 
              onClick={handleCloseDetailsDialog}
            ></div>

            {/* Modal */}
            <div className="inline-block align-bottom bg-white rounded-xl shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <h3 className="text-lg font-semibold text-gray-900 mr-3">
                      Détails de l'événement #{selectedDetails.id}
                    </h3>
                    <button
                      onClick={() => exportToPDF(selectedDetails)}
                      className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                      title="Exporter en PDF"
                    >
                      <DocumentArrowDownIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <button
                    onClick={handleCloseDetailsDialog}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                <div className="space-y-6">
                  {/* Informations générales */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Statut</p>
                      {getStatutBadge(selectedDetails.statut)}
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Type</p>
                      {getTypeBadge(selectedDetails.type)}
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Transport</p>
                      <p className="text-gray-900">{selectedDetails.transport ? 'Oui' : 'Non'}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Date de création</p>
                      <p className="text-gray-900">{formatDate(selectedDetails.created_at)}</p>
                    </div>
                    
                    {selectedDetails.beneficiaire && (
                      <div className="md:col-span-2">
                        <p className="text-sm font-medium text-gray-700 mb-2">Bénéficiaire</p>
                        <div className="flex items-start">
                          <UserIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-900">{selectedDetails.beneficiaire.nom}</p>
                            <p className="text-sm text-gray-600">
                              Type: {selectedDetails.beneficiaire.type === 'association' ? 'Association' : 'Particulier'}
                            </p>
                            <p className="text-sm text-gray-600">
                              Contact: {selectedDetails.beneficiaire.contact}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Détails du pack */}
                  {selectedDetails.type === 'enterrement' && selectedDetails.pack_enterrement && (
                    <>
                      <div className="border-t border-gray-300 pt-4">
                        <div className="flex items-center mb-3">
                          <CubeIcon className="h-5 w-5 text-blue-600 mr-2" />
                          <h3 className="text-lg font-semibold text-gray-900">
                            Pack d'enterrement: {selectedDetails.pack_enterrement.nom}
                          </h3>
                        </div>
                        <p className="text-gray-600 mb-4">
                          {selectedDetails.pack_enterrement.description || 'Aucune description'}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Matériels */}
                  {selectedDetails.materiels && selectedDetails.materiels.length > 0 && (
                    <>
                      <div className="border-t border-gray-300 pt-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                          Matériels ({selectedDetails.materiels.length})
                        </h3>
                        <div className="overflow-x-auto border border-gray-300 rounded-lg">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                  Matériel
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                  Quantité demandée
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                                  Quantité validée
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {selectedDetails.materiels.map((materiel, index) => (
                                <tr key={index} className="hover:">
                                  <td className="px-4 py-3">
                                    <div className="text-sm font-medium text-gray-900">{materiel.nom}</div>
                                    <div className="text-xs text-gray-500">{materiel.reference}</div>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {materiel.pivot.quantite_demandee}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {materiel.pivot.quantite_validee ? (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        {materiel.pivot.quantite_validee}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
                <button
                  onClick={() => exportToPDF(selectedDetails)}
                  className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition flex items-center"
                >
                  <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                  Exporter en PDF
                </button>
                <button
                  onClick={handleCloseDetailsDialog}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {openDeleteDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 0 bg-opacity-75 transition-opacity" 
              onClick={() => setOpenDeleteDialog(false)}
            ></div>

            <div className="inline-block align-bottom bg-white rounded-xl shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmer la suppression</h3>
                <p className="text-gray-600 mb-4">
                  Êtes-vous sûr de vouloir supprimer l'événement #{selectedEvenement?.id} ?
                  Cette action est irréversible.
                </p>
                {selectedEvenement?.statut !== 'en_attente' && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-yellow-800 text-sm">
                      <InformationCircleIcon className="h-5 w-5 inline mr-1" />
                      Seuls les événements en attente peuvent être supprimés.
                    </p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4  rounded-b-xl flex justify-end space-x-3">
                <button
                  onClick={() => setOpenDeleteDialog(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={selectedEvenement?.statut !== 'en_attente'}
                  className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar pour les messages */}
      {openSnackbar && (
        <div className={`fixed bottom-4 right-4 z-50 animate-slide-in-right`}>
          <div className={`rounded-lg shadow-lg p-4 max-w-sm ${message.type === 'success' ? 'bg-green-50 border-l-4 border-green-500' : 'bg-red-50 border-l-4 border-red-500'}`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {message.type === 'success' ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircleIcon className="h-5 w-5 text-red-500" />
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">
                  {message.type === 'success' ? 'Succès' : 'Erreur'}
                </p>
                <p className="mt-1 text-sm text-gray-700">{message.text}</p>
              </div>
              <button
                onClick={handleCloseSnackbar}
                className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-500"
              >
                <XCircleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Animation CSS pour snackbar */}
      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MyEvenements;